import { TooltipProvider } from "./utils-CLNKe1ar.js";
import { DEFAULT_BRANDS, DEFAULT_COLOR_SCHEME, ThemeScopeContainerContext, ThemeScopeSchemeContext, createCssVars, createTheme, defaultBrand, defaultTheme, useBrandState, useColorSchemeState } from "./themeScopeContext-BZGPdyG8.js";
import { createContext, use, useEffect, useInsertionEffect, useMemo, useRef, useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { jsx, jsxs } from "react/jsx-runtime";
import { agnes, brandSchema, exampleCustomer, harbor, kbcUi, keboola as keboola$1 } from "@keboola/brand-registry";
// src/components/ThemeProvider/AIGradientSvg.tsx
const AIGradientsSvg = () => /* @__PURE__ */ jsx("svg", {
	width: "0",
	height: "0",
	style: {
		position: "absolute",
		visibility: "hidden"
	},
	children: /* @__PURE__ */ jsxs("defs", { children: [/* @__PURE__ */ jsxs("linearGradient", {
		id: "aigradient-600",
		x1: "0%",
		y1: "0%",
		x2: "100%",
		y2: "100%",
		children: [
			/* @__PURE__ */ jsx("stop", {
				offset: "0%",
				stopColor: "#61E4FF"
			}),
			/* @__PURE__ */ jsx("stop", {
				offset: "50%",
				stopColor: "#1F8FFF"
			}),
			/* @__PURE__ */ jsx("stop", {
				offset: "100%",
				stopColor: "#7C5CFF"
			})
		]
	}), /* @__PURE__ */ jsxs("linearGradient", {
		id: "aigradient-700",
		x1: "0%",
		y1: "0%",
		x2: "100%",
		y2: "100%",
		children: [/* @__PURE__ */ jsx("stop", {
			offset: "0%",
			stopColor: "#075fb8"
		}), /* @__PURE__ */ jsx("stop", {
			offset: "100%",
			stopColor: "#5b2cb8"
		})]
	})] })
});
// src/components/ThemeProvider/context.tsx
const noop = () => void 0;
const defaultContext = {
	scheme: DEFAULT_COLOR_SCHEME,
	resolvedScheme: DEFAULT_COLOR_SCHEME,
	setScheme: noop,
	brandId: defaultTheme.id,
	setBrand: noop,
	themes: [defaultTheme],
	currentTheme: defaultTheme,
	brandTheme: defaultBrand.theme
};
const ThemeContext = createContext(defaultContext);
const useTheme = () => use(ThemeContext);
// src/components/ThemeProvider/useThemeTokens.ts
const brandThemeToTokens = (theme, scheme) => ({
	colors: scheme === "dark" ? theme.colorsDark : theme.colors,
	typography: theme.typography,
	radii: theme.radii,
	shadows: theme.shadows,
	spacing: theme.spacing,
	motion: theme.motion,
	gradient: theme.gradient,
	opacity: theme.opacity
});
const tokensToCssVars = ({ colors, typography, radii, shadows, spacing, motion, gradient, opacity }) => {
	const vars = createCssVars();
	vars.set("--background", colors?.background);
	vars.set("--foreground", colors?.foreground);
	vars.set("--muted", colors?.muted);
	vars.set("--muted-foreground", colors?.mutedForeground);
	vars.set("--border", colors?.border);
	vars.set("--input", colors?.input);
	vars.set("--input-background", colors?.inputBackground);
	vars.set("--ring", colors?.ring);
	vars.set("--ring-offset", colors?.ringOffset);
	vars.set("--primary", colors?.primary);
	vars.set("--primary-foreground", colors?.primaryForeground);
	vars.set("--secondary", colors?.secondary);
	vars.set("--secondary-foreground", colors?.secondaryForeground);
	vars.set("--accent", colors?.accent);
	vars.set("--accent-foreground", colors?.accentForeground);
	vars.set("--card", colors?.card);
	vars.set("--card-foreground", colors?.cardForeground);
	vars.set("--popover", colors?.popover);
	vars.set("--popover-foreground", colors?.popoverForeground);
	vars.set("--destructive", colors?.destructive);
	vars.set("--destructive-foreground", colors?.destructiveForeground);
	vars.set("--success", colors?.success);
	vars.set("--success-foreground", colors?.successForeground);
	vars.set("--warning", colors?.warning);
	vars.set("--warning-foreground", colors?.warningForeground);
	vars.set("--info", colors?.info);
	vars.set("--info-foreground", colors?.infoForeground);
	vars.set("--hover", colors?.hover);
	vars.set("--hover-foreground", colors?.hoverForeground);
	vars.set("--pressed", colors?.pressed);
	vars.set("--selected", colors?.selected);
	vars.set("--selected-foreground", colors?.selectedForeground);
	vars.set("--scrim", colors?.scrim);
	vars.set("--chart-1", colors?.chart?.chart1);
	vars.set("--chart-2", colors?.chart?.chart2);
	vars.set("--chart-3", colors?.chart?.chart3);
	vars.set("--chart-4", colors?.chart?.chart4);
	vars.set("--chart-5", colors?.chart?.chart5);
	vars.set("--sidebar", colors?.sidebar?.sidebar);
	vars.set("--sidebar-foreground", colors?.sidebar?.sidebarForeground);
	vars.set("--sidebar-primary", colors?.sidebar?.sidebarPrimary);
	vars.set("--sidebar-primary-foreground", colors?.sidebar?.sidebarPrimaryForeground);
	vars.set("--sidebar-accent", colors?.sidebar?.sidebarAccent);
	vars.set("--sidebar-accent-foreground", colors?.sidebar?.sidebarAccentForeground);
	vars.set("--sidebar-hover", colors?.sidebar?.sidebarHover);
	vars.set("--sidebar-hover-foreground", colors?.sidebar?.sidebarHoverForeground);
	vars.set("--sidebar-selected", colors?.sidebar?.sidebarSelected);
	vars.set("--sidebar-selected-foreground", colors?.sidebar?.sidebarSelectedForeground);
	vars.set("--sidebar-border", colors?.sidebar?.sidebarBorder);
	vars.set("--sidebar-ring", colors?.sidebar?.sidebarRing);
	vars.set("--font-sans", typography?.fontFamily?.trim() || void 0);
	vars.set("--font-display", typography?.fontDisplay?.trim() || void 0);
	vars.set("--font-mono", typography?.fontMono?.trim() || void 0);
	vars.set("--font-weight-normal", typography?.weight?.normal);
	vars.set("--font-weight-medium", typography?.weight?.medium);
	vars.set("--font-weight-semibold", typography?.weight?.semibold);
	vars.set("--font-weight-bold", typography?.weight?.bold);
	vars.set("--letter-spacing-chrome", typography?.letterSpacing?.chrome);
	vars.set("--type-display-size", typography?.typeScale?.display?.size);
	vars.set("--type-display-weight", typography?.typeScale?.display?.weight);
	vars.set("--type-display-leading", typography?.typeScale?.display?.leading);
	vars.set("--type-display-tracking", typography?.typeScale?.display?.tracking);
	vars.set("--type-heading-size", typography?.typeScale?.heading?.size);
	vars.set("--type-heading-weight", typography?.typeScale?.heading?.weight);
	vars.set("--type-heading-leading", typography?.typeScale?.heading?.leading);
	vars.set("--type-heading-tracking", typography?.typeScale?.heading?.tracking);
	vars.set("--type-body-size", typography?.typeScale?.body?.size);
	vars.set("--type-body-weight", typography?.typeScale?.body?.weight);
	vars.set("--type-body-leading", typography?.typeScale?.body?.leading);
	vars.set("--type-body-tracking", typography?.typeScale?.body?.tracking);
	vars.set("--radius-sm", radii?.sm);
	vars.set("--radius-md", radii?.md);
	vars.set("--radius-lg", radii?.lg);
	vars.set("--radius-card", radii?.card);
	vars.set("--radius-popover", radii?.popover);
	vars.set("--radius-modal", radii?.modal);
	vars.set("--radius-sheet", radii?.sheet);
	vars.set("--radius-toast", radii?.toast);
	vars.set("--shadow-sm", shadows?.sm);
	vars.set("--shadow-md", shadows?.md);
	vars.set("--shadow-lg", shadows?.lg);
	vars.set("--shadow-lift", shadows?.lift);
	vars.set("--space-gutter", spacing?.gutter);
	vars.set("--space-inset", spacing?.inset);
	vars.set("--motion-duration", motion?.duration);
	vars.set("--motion-easing", motion?.easing);
	vars.set("--gradient-brand", gradient?.brand);
	vars.set("--disabled-opacity", opacity?.disabled);
	return vars.expose();
};
const setTokens = (node, vars) => {
	for (const [name, value] of Object.entries(vars)) node.style.setProperty(name, value);
};
const removeTokens = (node, vars) => {
	for (const name of Object.keys(vars)) node.style.removeProperty(name);
};
/**
* Writes a brand's token overrides as inline custom properties.
*
* - `target` defaults to `<html>`; an element scopes them to its subtree (`ThemeScope`).
* - A brand injects resolved `--color-*` aliases that `[data-theme='dark']` does not re-point,
*   so a nested scheme must re-inject its own values or light colours leak into dark.
*/
const useThemeTokens = (tokens, target) => {
	useInsertionEffect(() => {
		if (typeof document === "undefined") return;
		const node = target === void 0 ? document.documentElement : target;
		if (!node) return;
		const vars = tokensToCssVars(tokens);
		setTokens(node, vars);
		return () => removeTokens(node, vars);
	}, [tokens, target]);
};
// src/components/ThemeProvider/ThemeProvider.tsx
const ThemeProvider = ({ defaultScheme, scheme, onSchemeChange, defaultBrandId, brandId, onBrandIdChange, brands = DEFAULT_BRANDS, children }) => {
	const colorSchemeState = useColorSchemeState({
		defaultScheme,
		scheme,
		onSchemeChange
	});
	const brandState = useBrandState({
		defaultBrandId,
		brandId,
		onBrandIdChange
	});
	const brand = useMemo(() => brands.find((item) => item.id === brandState.brandId) ?? defaultBrand, [brands, brandState.brandId]);
	const themes = useMemo(() => brands.map(createTheme), [brands]);
	const currentTheme = useMemo(() => createTheme(brand), [brand]);
	const themeTokens = useMemo(() => brandThemeToTokens(brand.theme, colorSchemeState.resolvedScheme), [brand, colorSchemeState.resolvedScheme]);
	useThemeTokens(themeTokens);
	const { scheme: activeScheme, resolvedScheme, setScheme } = colorSchemeState;
	const { brandId: activeBrandId, setBrand } = brandState;
	const context = useMemo(() => ({
		scheme: activeScheme,
		resolvedScheme,
		setScheme,
		brandId: activeBrandId,
		setBrand,
		themes,
		currentTheme,
		brandTheme: brand.theme
	}), [
		activeScheme,
		resolvedScheme,
		setScheme,
		activeBrandId,
		setBrand,
		themes,
		currentTheme,
		brand
	]);
	return /* @__PURE__ */ jsxs(ThemeContext, {
		value: context,
		children: [/* @__PURE__ */ jsx(AIGradientsSvg, {}), /* @__PURE__ */ jsx(TooltipProvider, { children })]
	});
};
// src/components/ThemeProvider/ThemeScope.tsx
const DARK_QUERY = "(prefers-color-scheme: dark)";
/** The scope's themed portal container, or `null` outside a `ThemeScope`. */
const useThemeScopeContainer = () => use(ThemeScopeContainerContext);
const useResolvedScheme = (scheme) => {
	const [systemPrefersDark, setSystemPrefersDark] = useState(() => globalThis.matchMedia?.(DARK_QUERY).matches ?? false);
	useEffect(() => {
		const query = globalThis.matchMedia?.(DARK_QUERY);
		if (!query) return;
		const onChange = (event) => setSystemPrefersDark(event.matches);
		query.addEventListener("change", onChange);
		return () => query.removeEventListener("change", onChange);
	}, []);
	if (scheme === "system") return systemPrefersDark ? "dark" : "light";
	return scheme;
};
/**
* Runs one subtree in its own colour scheme.
*
* - Also re-injects the brand's tokens for its scheme: a bare `data-theme` only re-points raw
*   tokens, not the brand's inline vars inherited from `<html>`.
* - Renders no wrapper: one would break direct-child selectors like `[data-fill=true] > &`.
*/
const ThemeScope = ({ scheme, className, children }) => {
	const { brandTheme } = useTheme();
	const resolvedScheme = useResolvedScheme(scheme);
	const [portalContainer, setPortalContainer] = useState(null);
	const tokens = useMemo(() => brandThemeToTokens(brandTheme, resolvedScheme), [brandTheme, resolvedScheme]);
	const style = useMemo(() => tokensToCssVars(tokens), [tokens]);
	useThemeTokens(tokens, portalContainer);
	const schemeRef = useRef(resolvedScheme);
	schemeRef.current = resolvedScheme;
	useEffect(() => {
		const container = document.createElement("div");
		container.dataset.themeScopePortal = "";
		container.dataset.theme = schemeRef.current;
		document.body.append(container);
		setPortalContainer(container);
		return () => container.remove();
	}, []);
	useEffect(() => {
		portalContainer?.setAttribute("data-theme", resolvedScheme);
	}, [portalContainer, resolvedScheme]);
	return /* @__PURE__ */ jsx(ThemeScopeContainerContext, {
		value: portalContainer,
		children: /* @__PURE__ */ jsx(ThemeScopeSchemeContext, {
			value: resolvedScheme,
			children: /* @__PURE__ */ jsx(Slot, {
				"data-theme": resolvedScheme,
				className,
				style,
				children
			})
		})
	});
};
export { ThemeProvider, ThemeScope, agnes, brandSchema, exampleCustomer, harbor, kbcUi, keboola$1 as keboola, useTheme, useThemeScopeContainer };

//# sourceMappingURL=ThemeProvider-DHf_Tvwk.js.map